import { useCallback, useEffect, useRef, useState } from "react";
import AppShell from "../components/layout/AppShell";
import Button from "../components/common/Button";
import Spinner from "../components/common/Spinner";
import EmailDraftModal from "../components/email/EmailDraftModal";
import LeadCampaignPanel from "../components/email/LeadCampaignPanel";
import AiWritingPanel from "../components/email/AiWritingPanel";
import {
  getEmailSettings,
  saveEmailSettings,
  syncEmailNow,
  newEmailToList,
  emailHistory,
  listContacts,
  unsubscribeContact,
  importContacts,
  draftNewsletterNow,
} from "../api/email";
import { timeAgo } from "../utils/contact";
import { useLiveRefresh } from "../utils/live";
import { notifyCountsChanged } from "../utils/navCounts";

// Your email list and how it's drafted. Emails themselves are approved from
// Follow-ups; this page is the setup, the list and the history.

export default function AdminEmail() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [open, setOpen] = useState(null); // {id, note}

  const load = useCallback(async () => {
    try {
      const [d, h] = await Promise.all([getEmailSettings(), emailHistory().catch(() => [])]);
      setData(d);
      setHistory(h);
    } catch (e) {
      setError(e.message || "Could not load email settings.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  useLiveRefresh(load);

  async function newsletter() {
    setError("");
    try {
      const d = await draftNewsletterNow();
      setOpen({ id: d.id, note: d.ai_note || "" });
    } catch (e) {
      setError(e.message || "Couldn't write the newsletter.");
    }
  }

  async function compose() {
    setError("");
    try {
      const d = await newEmailToList();
      setOpen({ id: d.id });
    } catch (e) {
      setError(e.message || "Could not start a new email.");
    }
  }

  return (
    <AppShell title="Email">
      {error && <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {notice && (
        <div className="mb-3 rounded-xl border border-birdie-green/25 bg-birdie-green/10 p-3 text-sm text-birdie-green">
          {notice}
        </div>
      )}
      {!data ? (
        !error && (
          <div className="py-16 flex justify-center">
            <Spinner label="Loading..." />
          </div>
        )
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-birdie-charcoal/65 px-0.5">
            Thank-yous, follow-ups to quiet leads and the monthly newsletter are drafted for you and wait in{" "}
            <strong>Follow-ups</strong> until you approve them. Nothing sends on its own.
          </p>

          <Section title="Waivers from your website">
            <WaiverSync
              data={data}
              onSynced={(msg) => {
                setNotice(msg);
                load();
                notifyCountsChanged();
              }}
              onError={setError}
            />
          </Section>

          <Section title="How emails are sent">
            <Sender data={data} />
          </Section>

          <Section title="Your list">
            <ListPanel data={data} onCompose={compose} onChanged={load} onNotice={setNotice} onError={setError} />
          </Section>

          <Section title="Settings">
            <SettingsForm
              settings={data.settings}
              onSaved={(s) => {
                setData((d) => ({ ...d, settings: s }));
                setNotice("Saved.");
              }}
              onError={setError}
            />
          </Section>

          <Section title="Drafted for you">
            <Rules
              data={data}
              onChange={(s) => setData((d) => ({ ...d, settings: s }))}
              onError={setError}
              onNewsletter={newsletter}
            />
          </Section>

          <Section title="No-reply follow-ups to quiet leads">
            <LeadCampaignPanel onNotice={setNotice} onError={setError} />
          </Section>

          <Section title="Claude">
            <AiWritingPanel onNotice={setNotice} onError={setError} />
          </Section>

          <Section title="Sent">
            {history.length === 0 ? (
              <p className="text-sm text-birdie-charcoal/55">Nothing sent yet.</p>
            ) : (
              <ul className="divide-y divide-birdie-green/10">
                {history.map((h) => (
                  <li key={h.id}>
                    <button
                      type="button"
                      onClick={() => h.status === "sent" && setOpen({ id: h.id })}
                      className="w-full flex items-start justify-between gap-3 py-2.5 text-left"
                    >
                      <span className="min-w-0">
                        <span
                          className={`block text-sm font-medium truncate ${
                            h.status === "dismissed" ? "text-birdie-charcoal/40 line-through" : "text-birdie-charcoal"
                          }`}
                        >
                          {h.subject || "(no subject)"}
                        </span>
                        <span className="block text-[11px] text-birdie-charcoal/50">
                          {h.kind_label}
                          {h.customer_name ? ` · ${h.customer_name}` : ""}
                        </span>
                      </span>
                      <span className="shrink-0 text-right text-[11px] text-birdie-charcoal/50">
                        {h.status === "dismissed" ? "Dismissed" : `Sent to ${h.sent}`}
                        {Number(h.failed) > 0 && <span className="block text-amber-700">{h.failed} failed</span>}
                        <span className="block">{timeAgo(h.finished_at || h.approved_at || h.created_at)}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Section>

        </div>
      )}

      {open && (
        <EmailDraftModal
          draftId={open.id}
          note={open.note}
          onClose={(changed) => {
            setOpen(null);
            if (changed) load().then(notifyCountsChanged);
          }}
        />
      )}
    </AppShell>
  );
}

function WaiverSync({ data, onSynced, onError }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const sync = data.sync || {};
  // The account the server reported; the Cloud Run default until it has said.
  const account = String(sync.account || "").includes("@")
    ? sync.account
    : "1076807640806-compute@developer.gserviceaccount.com";
  const command = `gcloud projects add-iam-policy-binding ${data.project} --member=serviceAccount:${account} --role=roles/datastore.viewer`;

  async function check() {
    setBusy(true);
    onError("");
    try {
      const r = await syncEmailNow();
      const s = r.sync || {};
      if (s.status === "ok") {
        const parts = [
          s.last_imported ? `${s.last_imported} new waiver${s.last_imported === 1 ? "" : "s"}` : "No new waivers",
          r.new_drafts ? `${r.new_drafts} email${r.new_drafts === 1 ? "" : "s"} drafted (see Follow-ups)` : null,
        ].filter(Boolean);
        onSynced(`${parts.join(" · ")}.`);
      } else {
        onSynced("");
      }
    } catch (e) {
      onError(e.message || "Couldn't check for waivers.");
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* the text is selectable anyway */
    }
  }

  return (
    <div className="text-sm text-birdie-charcoal/80 space-y-3">
      {sync.status === "ok" ? (
        <p>
          <span className="text-birdie-green font-semibold">✓ Connected.</span> {data.stats.waivers} waivers so far
          {data.stats.unmatched > 0 && (
            <span className="text-birdie-charcoal/55">
              {" "}
              ({data.stats.unmatched} signed on a day with no booked event)
            </span>
          )}
          . Checked {timeAgo(sync.last_ok_at)}.
        </p>
      ) : sync.status === "needs_access" ? (
        <div className="space-y-2">
          <p>
            <strong>One-time setup.</strong> Let the dashboard read the waivers your website collects. Run this once
            in PowerShell (or Google Cloud Shell):
          </p>
          <pre className="whitespace-pre-wrap break-all rounded-xl bg-birdie-charcoal text-white text-xs p-3 select-all">
            {command}
          </pre>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={copy}>
              {copied ? "Copied ✓" : "Copy"}
            </Button>
          </div>
          <p className="text-xs text-birdie-charcoal/55">
            It's read-only: the dashboard can see waivers but never change them. Then tap Check now.
          </p>
        </div>
      ) : sync.status === "error" ? (
        <p className="text-amber-800">{sync.message || "Something went wrong reading waivers."}</p>
      ) : (
        <p>Waivers signed on your QR code page come in here automatically every hour.</p>
      )}
      <Button size="sm" variant={sync.status === "ok" ? "secondary" : "primary"} onClick={check} disabled={busy}>
        {busy ? "Checking…" : "Check now"}
      </Button>
    </div>
  );
}

// Resend keeps list emails off the Workspace account, so complaints can't
// get the account (and the quote emails) blocked.
function Sender({ data }) {
  const usage = `${data.sent_24h} of ${data.daily_cap.toLocaleString()} sent in the last 24 hours. Anything over waits for the next day.`;
  if (data.provider === "Resend") {
    return (
      <div className="text-sm text-birdie-charcoal/80 space-y-1">
        <p>
          <span className="text-birdie-green font-semibold">✓ Through Resend</span> from{" "}
          <strong>{data.sender_email}</strong>. Replies go to {data.reply_to}.
        </p>
        <p className="text-xs text-birdie-charcoal/55">
          Your Gmail and quote emails are kept separate from these. {usage}
        </p>
      </div>
    );
  }
  return (
    <div className="text-sm text-birdie-charcoal/80 space-y-2">
      <p>
        {data.sender_ready ? (
          <>
            Right now these go out through your Gmail ({data.sender_email}), the same account as your quote emails.
          </>
        ) : (
          <span className="text-red-700">Email sending isn't set up on the server.</span>
        )}
      </p>
      <p className="font-semibold">Move them to Resend (free, one-time):</p>
      <ol className="list-decimal pl-5 space-y-1 text-xs text-birdie-charcoal/70">
        <li>
          Sign up at resend.com, then <strong>Domains → Add domain</strong>: <code>updates.birdiebus.com</code>.
        </li>
        <li>Add the DNS records it shows wherever birdiebus.com's DNS is managed, then wait for “Verified”.</li>
        <li>
          <strong>API Keys → Create</strong> (Sending access).
        </li>
        <li>
          In Google Cloud → Cloud Run → <strong>birdie-bus-backend</strong> → Edit &amp; deploy new revision →
          Variables: add <code>RESEND_API_KEY</code> with that key, then Deploy.
        </li>
      </ol>
      {data.sender_ready && <p className="text-xs text-birdie-charcoal/55">{usage}</p>}
    </div>
  );
}

function ListPanel({ data, onCompose, onChanged, onNotice, onError }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const fileRef = useRef(null);

  const search = useCallback(async (term) => {
    try {
      setRows(await listContacts(term));
    } catch {
      setRows([]);
    }
  }, []);

  useEffect(() => {
    if (rows === null && !q) return undefined;
    const t = setTimeout(() => search(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q, search]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    onError("");
    try {
      const r = await importContacts(await file.text());
      onNotice(`Imported: ${r.added} new, ${r.updated} already on your list, ${r.skipped} skipped.`);
      onChanged();
      if (rows !== null) search(q.trim());
    } catch (err) {
      onError(err.message || "Couldn't import that file.");
    } finally {
      setImporting(false);
    }
  }

  async function unsub(c) {
    if (!window.confirm(`Stop emailing ${c.email}?`)) return;
    try {
      await unsubscribeContact(c.id);
      search(q.trim());
      onChanged();
    } catch (err) {
      onError(err.message || "Couldn't unsubscribe them.");
    }
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <Stat label="People" value={data.stats.contacts} />
        <Stat label="Get your news" value={data.stats.opted_in} />
        <Stat label="Unsubscribed" value={data.stats.unsubscribed} />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={onCompose}>
          + New email to your list
        </Button>
        <Button size="sm" variant="secondary" onClick={() => fileRef.current?.click()} disabled={importing}>
          {importing ? "Importing…" : "Import a list (CSV)"}
        </Button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} />
      </div>
      {data.stats.from_leads > 0 && (
        <p className="text-[11px] text-birdie-charcoal/55">
          Your list also includes {data.stats.from_leads} lead{data.stats.from_leads === 1 ? "" : "s"} who finished the
          no-reply emails.
        </p>
      )}
      <p className="text-[11px] text-birdie-charcoal/45">
        Moving off Beehiiv? Export your subscribers there as a CSV and import it here.
      </p>

      {rows === null ? (
        <button type="button" onClick={() => search("")} className="text-sm font-semibold text-birdie-green">
          Show people
        </button>
      ) : (
        <div>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or email"
            aria-label="Search contacts"
            className="w-full rounded-xl border border-birdie-green/20 bg-white px-3 py-2 text-sm mb-2"
          />
          {rows.length === 0 ? (
            <p className="text-sm text-birdie-charcoal/55">Nobody matches.</p>
          ) : (
            <ul className="divide-y divide-birdie-green/10">
              {(showAll ? rows : rows.slice(0, 12)).map((c) => (
                <li key={c.id} className="py-2 flex items-center justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block text-sm text-birdie-charcoal truncate">
                      {[c.first_name, c.last_name].filter(Boolean).join(" ") || c.email}
                    </span>
                    <span className="block text-[11px] text-birdie-charcoal/50 truncate">
                      {c.email} · {SOURCE[c.source] || c.source}
                      {c.waivers > 1 ? ` · ${c.waivers} waivers` : ""}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    {c.unsubscribed_at ? (
                      <span className="text-[11px] text-birdie-charcoal/45">Unsubscribed</span>
                    ) : (
                      <>
                        {c.marketing_ok && (
                          <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-birdie-green/15 text-birdie-green mr-2">
                            News ✓
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => unsub(c)}
                          className="text-[11px] font-semibold text-birdie-charcoal/55 hover:text-red-700"
                        >
                          Unsubscribe
                        </button>
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {!showAll && rows.length > 12 && (
            <button type="button" onClick={() => setShowAll(true)} className="mt-1 text-sm font-semibold text-birdie-green">
              Show {rows.length - 12} more
            </button>
          )}
          {rows.length >= 60 && showAll && (
            <p className="text-[11px] text-birdie-charcoal/45 mt-1">Showing the newest 60. Search to find anyone else.</p>
          )}
        </div>
      )}
    </div>
  );
}

const SOURCE = {
  waiver: "waiver",
  newsletter: "website signup",
  notify: "website signup",
  customer: "customer",
  import: "imported",
  lead: "lead",
};

function SettingsForm({ settings, onSaved, onError }) {
  const [form, setForm] = useState(settings);
  const [busy, setBusy] = useState(false);
  useEffect(() => setForm(settings), [settings]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const dirty = ["mailing_address", "from_name", "signature", "review_url", "booking_url"].some(
    (k) => (form[k] || "") !== (settings[k] || "")
  );

  async function save() {
    setBusy(true);
    onError("");
    try {
      const r = await saveEmailSettings({
        mailing_address: form.mailing_address,
        from_name: form.from_name,
        signature: form.signature,
        review_url: form.review_url,
        booking_url: form.booking_url,
      });
      onSaved(r.settings);
    } catch (e) {
      onError(e.message || "Couldn't save.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <Field
        label="Mailing address (required)"
        hint="The law (CAN-SPAM) requires a postal address at the bottom of these emails. A PO box is fine."
      >
        <input value={form.mailing_address || ""} onChange={set("mailing_address")} className={INPUT} placeholder="PO Box 123, Naples, FL 34102" />
      </Field>
      <Field label="From name" hint="What people see in their inbox.">
        <input value={form.from_name || ""} onChange={set("from_name")} className={INPUT} />
      </Field>
      <Field label="Sign-off" hint="Added to the bottom of new drafts.">
        <textarea value={form.signature || ""} onChange={set("signature")} rows={2} className={INPUT} />
      </Field>
      <Field label="Google review link" hint="birdiebus.com/review counts the click, then opens your Google review box.">
        <input value={form.review_url || ""} onChange={set("review_url")} className={INPUT} inputMode="url" />
      </Field>
      <Field label="Booking link" hint="Where “Check a date” buttons go.">
        <input value={form.booking_url || ""} onChange={set("booking_url")} className={INPUT} inputMode="url" />
      </Field>
      <Button size="sm" onClick={save} disabled={busy || !dirty}>
        {busy ? "Saving…" : "Save"}
      </Button>
    </div>
  );
}

function Rules({ data, onChange, onError, onNewsletter }) {
  const [writing, setWriting] = useState(false);
  async function toggle(key, value) {
    const prev = data.settings;
    onChange({ ...prev, rules: { ...prev.rules, [key]: value } });
    try {
      const r = await saveEmailSettings({ rules: { [key]: value } });
      onChange(r.settings);
    } catch (e) {
      onChange(prev);
      onError(e.message || "Couldn't save that.");
    }
  }
  return (
    <div>
      <ul className="divide-y divide-birdie-green/10">
        {data.kinds.map((k) => (
          <li key={k.key} className="flex items-center justify-between gap-3 py-2.5">
            <span className="min-w-0">
              <span className="block text-sm text-birdie-charcoal">{k.label}</span>
              <span className="block text-[11px] text-birdie-charcoal/50">{k.when}</span>
            </span>
            <Switch checked={data.settings.rules[k.key] !== false} onChange={(v) => toggle(k.key, v)} label={k.label} />
          </li>
        ))}
      </ul>
      <Button
        size="sm"
        variant="secondary"
        className="mt-2"
        disabled={writing}
        onClick={async () => {
          setWriting(true);
          await onNewsletter();
          setWriting(false);
        }}
      >
        {writing ? "Writing…" : "Write this month's newsletter now"}
      </Button>
      <p className="text-[11px] text-birdie-charcoal/45 mt-2">
        Drafts wait in Follow-ups and buzz your phone once. Marketing emails skip anyone you've emailed in the last
        week, and unsubscribes are always respected.
      </p>
    </div>
  );
}

const INPUT =
  "w-full rounded-xl border border-birdie-green/20 bg-white px-3 py-2 text-sm text-birdie-charcoal focus:outline-none focus:ring-2 focus:ring-birdie-mustard/40";

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-birdie-charcoal/70 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-birdie-charcoal/45 mt-1">{hint}</span>}
    </label>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-birdie-cream/70 rounded-xl px-3 py-2">
      <div className="text-[11px] text-birdie-charcoal/55">{label}</div>
      <div className="text-lg font-semibold text-birdie-charcoal">{Number(value || 0).toLocaleString()}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="bg-white border border-birdie-green/12 rounded-2xl p-4">
      <h2 className="font-accent text-base text-birdie-charcoal mb-2">{title}</h2>
      {children}
    </section>
  );
}

function Switch({ checked, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 min-w-0 min-h-0 shrink-0 rounded-full transition ${
        checked ? "bg-birdie-green" : "bg-birdie-charcoal/20"
      }`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
          checked ? "left-[22px]" : "left-0.5"
        }`}
      />
    </button>
  );
}
